// CARO. Casa de estilo — translated URL segments (shared, pure).
//
// The app's "logical" paths are the Spanish ones (`/shop/casa`, `/contacto`):
// they are what the code, STATIC_PAGES, the sitemap and the tests key on, and
// they are also the public ES URLs. EN and FR publish the same pages under
// translated segments (`/fr/boutique/maison`, `/en/contact`). This module is
// the ONLY translation table (client and Worker both go through
// localizedPath/stripLocalePrefix in @/lib/seo, which call in here).
//
// The table is a small tree keyed by logical segment, so a segment translates
// according to its parent (`shop/casa` → `boutique/maison`, `the-edit/casa` →
// `the-edit/maison`). Translation walks a path segment by segment and stops at
// the first segment that is not in the tree: params, product slugs and the
// functional routes (cart, checkout, legal, admin, newsletter/baja, api,
// media…) pass through untouched. Paths here never carry the locale prefix.

import { DEFAULT_LOCALE } from '@/config/site';
import { articleEsSlug, articleSlugFor } from '@/lib/articles';
import { seasonEsSlug, seasonSlugFor } from '@/lib/colorimetry/slugs';
import { styleEsSlug, styleSlugFor } from '@/lib/colorimetry/styleSlugs';
import { serviceEsSlug, serviceSlugFor } from '@/content/services';
import type { Locale } from '@/types';

/** Locales whose URLs are translated (every locale but the default, ES). */
type TranslatedLocale = Exclude<Locale, 'es'>;

/**
 * Translates the VALUE of the dynamic segment that follows a node (e.g. the
 * article slug after `the-edit/post`). Both directions must be inverses, and
 * `toLogical` must also accept the logical value itself (old URLs).
 */
export interface ParamTranslator {
  toPublic(locale: Locale, value: string): string;
  toLogical(locale: Locale, value: string): string;
}

interface RouteNode {
  /** Public segment per translated locale (ES = the logical key itself). */
  public: Record<TranslatedLocale, string>;
  /** Known logical sub-segments. */
  children?: Record<string, RouteNode>;
  /** Translator for the dynamic segment right after this node. */
  param?: ParamTranslator;
}

/** Article slugs after `the-edit/post`: per-locale slugs (ARTICLE_SLUGS in content/articles.ts). */
const ARTICLE_SLUG: ParamTranslator = {
  toPublic: articleSlugFor,
  toLogical: articleEsSlug,
};

/** COLORIMETRY ME! season slugs after `estudio-de-estilo/colorimetria` (lib/colorimetry/slugs.ts). */
const SEASON_SLUG: ParamTranslator = {
  toPublic: seasonSlugFor,
  toLogical: seasonEsSlug,
};

/** Silhouette and face result slugs (lib/colorimetry/styleSlugs.ts). */
const SHAPE_SLUG: ParamTranslator = { toPublic: styleSlugFor('silhouette'), toLogical: styleEsSlug('silhouette') };
const FACE_SLUG: ParamTranslator = { toPublic: styleSlugFor('face'), toLogical: styleEsSlug('face') };

/** The three style tests under the Estudio de estilo (and under the legacy /tests-de-estilo node). */
const TEST_GAMES: Record<string, RouteNode> = {
  colorimetria: { public: { en: 'colour-analysis', fr: 'colorimetrie' }, param: SEASON_SLUG },
  morfologia: { public: { en: 'body-shape', fr: 'morphologie' }, param: SHAPE_SLUG },
  'forma-de-cara': { public: { en: 'face-shape', fr: 'forme-du-visage' }, param: FACE_SLUG },
};

/** Same public segment in every translated locale (brand words, already English). */
const same = (segment: string): Record<TranslatedLocale, string> => ({ en: segment, fr: segment });

/** Segment table validated by the owner (2026-10-16); see docs/plan-urls-traducidas.md. */
const ROUTE_TREE: Record<string, RouteNode> = {
  shop: {
    public: { en: 'shop', fr: 'boutique' },
    children: {
      estilo: { public: { en: 'style', fr: 'style' } },
      beauty: { public: { en: 'beauty', fr: 'beaute' } },
      casa: { public: { en: 'home', fr: 'maison' } },
      hallazgos: { public: { en: 'finds', fr: 'trouvailles' } },
      // Pack pages (`/shop/pack/<slug>`, docs/plan-packs.md); the slug stays Spanish.
      pack: { public: { en: 'bundle', fr: 'coffret' } },
    },
  },
  caro: {
    public: same('caro'),
    children: {
      filosofia: { public: { en: 'philosophy', fr: 'philosophie' } },
      // One page per service (content/services.ts SERVICE_PAGES).
      servicios: { public: { en: 'services', fr: 'services' }, param: { toPublic: serviceSlugFor, toLogical: serviceEsSlug } },
    },
  },
  contacto: { public: { en: 'contact', fr: 'contact' } },
  'the-edit': {
    public: same('the-edit'),
    children: {
      post: { public: { en: 'post', fr: 'article' }, param: ARTICLE_SLUG },
      estilo: { public: { en: 'style', fr: 'style' } },
      belleza: { public: { en: 'beauty', fr: 'beaute' } },
      bienestar: { public: { en: 'wellbeing', fr: 'bien-etre' } },
      casa: { public: { en: 'home', fr: 'maison' } },
      'mujer-40': { public: { en: 'women-40', fr: 'femmes-40' } },
    },
  },
  // The Estudio de estilo (docs/plan-estudio-estilo.md, fase 0): one root for every interactive page. Today
  // its home, the three style tests with their result pages (season slugs in lib/colorimetry/slugs.ts,
  // silhouette and face slugs in lib/colorimetry/styleSlugs.ts) and the guide «Combinar colores»
  // (docs/plan-estudio-p1-guia.md) and «Mi estudio» (docs/plan-estudio-p2-muneco.md): only here, not under the
  // legacy nodes that share TEST_GAMES.
  'estudio-de-estilo': {
    public: { en: 'style-studio', fr: 'studio-de-style' },
    children: {
      ...TEST_GAMES,
      'combinar-colores': { public: { en: 'how-to-combine-colours', fr: 'associer-les-couleurs' } },
      // «Mi estudio» (docs/plan-estudio-p2-muneco.md): noindex, the doll in `?p=`.
      'mi-estudio': { public: { en: 'my-studio', fr: 'mon-studio' } },
    },
  },
  // Where the tests lived from 2026-10-23 to 2026-10-26: kept only so old EN/FR URLs resolve to a logical
  // path before their one-hop 301 (worker/lib/localeRedirect.ts). Nothing links here.
  'tests-de-estilo': {
    public: { en: 'style-quizzes', fr: 'tests-de-style' },
    children: TEST_GAMES,
  },
  // Where the games lived until 2026-10-23: kept only so old EN/FR URLs resolve to a logical path before
  // their one-hop 301 (worker/lib/localeRedirect.ts LEGACY). Nothing links here.
  'colorimetry-me': {
    public: same('colorimetry-me'),
    children: {
      estacion: { public: { en: 'season', fr: 'saison' }, param: SEASON_SLUG },
      silueta: { public: { en: 'silhouette', fr: 'silhouette' }, param: SHAPE_SLUG },
      rostro: { public: { en: 'face', fr: 'visage' }, param: FACE_SLUG },
      'silueta-y-rostro': { public: { en: 'silhouette-and-face', fr: 'silhouette-et-visage' } },
    },
  },
};

/**
 * Logical key of a public segment among `siblings`, for `locale`. The locale's
 * own public segments win; the logical (Spanish) keys are accepted too so old
 * URLs (`/fr/contacto`) resolve to their logical path. Undefined: not in the tree.
 */
function logicalKey(siblings: Record<string, RouteNode>, locale: TranslatedLocale, segment: string): string | undefined {
  for (const [key, node] of Object.entries(siblings)) {
    if (node.public[locale] === segment) return key;
  }
  return Object.hasOwn(siblings, segment) ? segment : undefined;
}

type Direction = 'public' | 'logical';

/**
 * Walk `path` (no locale prefix) through the tree, translating each known
 * segment. Query string / hash are split off and re-attached; empty segments
 * (leading and trailing slashes) are kept as they are. `params: false` leaves
 * dynamic values alone (route patterns: `:slug` is not a value).
 */
function translate(locale: Locale, path: string, to: Direction, params = true): string {
  if (locale === DEFAULT_LOCALE) return path;
  const lang = locale as TranslatedLocale;
  const cut = path.search(/[?#]/);
  const pathname = cut === -1 ? path : path.slice(0, cut);
  const suffix = cut === -1 ? '' : path.slice(cut);

  let siblings: Record<string, RouteNode> | undefined = ROUTE_TREE;
  let param: ParamTranslator | undefined;
  const out = pathname.split('/').map((segment) => {
    if (segment === '') return segment;
    if (param) {
      const value = params ? (to === 'public' ? param.toPublic(locale, segment) : param.toLogical(locale, segment)) : segment;
      param = undefined;
      siblings = undefined;
      return value;
    }
    const key = siblings && (to === 'public' ? (Object.hasOwn(siblings, segment) ? segment : undefined) : logicalKey(siblings, lang, segment));
    const node = key !== undefined ? siblings![key] : undefined;
    if (!node) {
      // Unknown segment: it and everything after it pass through.
      siblings = undefined;
      return segment;
    }
    siblings = node.children;
    param = node.param;
    return to === 'public' ? node.public[lang] : key!;
  });
  return out.join('/') + suffix;
}

/** Public path of a logical path for `locale` (`/shop/casa/x` → fr `/boutique/maison/x`). ES: unchanged. */
export function toPublicPath(locale: Locale, logicalPath: string): string {
  return translate(locale, logicalPath, 'public');
}

/**
 * Logical path of a public path for `locale` (fr `/boutique/maison/x` →
 * `/shop/casa/x`). Also accepts the logical (Spanish) segments, so an old URL
 * like fr `/contacto` maps to `/contacto`: compare toPublicPath of the result
 * with the request to detect it. ES: unchanged.
 */
export function toLogicalPath(locale: Locale, publicPath: string): string {
  return translate(locale, publicPath, 'logical');
}

/**
 * A react-router pattern with its static segments translated for `locale`
 * (`/the-edit/post/:slug` → fr `/the-edit/article/:slug`). Params stay as they
 * are: `/shop/:category` gives `/boutique/:category`, whose matched value is the
 * PUBLIC category (`maison`), to be mapped back with toLogicalPath.
 */
export function logicalRoutePattern(locale: Locale, pattern: string): string {
  return translate(locale, pattern, 'public', false);
}
